import { FunctionComponent, useEffect, useRef, useState } from "react";
import { AccordionType, useDashboardContext } from "./dashboardContext";
import { Box } from "@mui/material";
import FilterAccordion from "../../components/FilterAccordion/FilterAccordion";
import { Division } from "../../models/Division";
import { Section } from "../../models/Section";
import { Magasin } from "../../models/Magasin";

export const DashboardProductAccordions: FunctionComponent = () => {
    const {
        expandedAccordions,
        divisions,
        sections,
        selectedDivision,
        selectedSection,
        setSelectedDivision,
        setSelectedSection,
        setExpandedAccordions,
    } = useDashboardContext();
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerHeight, setContainerHeight] = useState(0);

    useEffect(() => {
        const resizeObserverHandler = (entries: ResizeObserverEntry[]) => {
            for (let entry of entries) {
                const { height } = entry.contentRect;
                console.log("Container height:", height);
                setContainerHeight(height);
            }
        };

        const resizeObserver = new ResizeObserver(resizeObserverHandler);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }
        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <Box
            ref={containerRef}
            sx={{
                height: "100%",
            }}
        >
            <FilterAccordion
                title="Divisions"
                expanded={expandedAccordions === AccordionType.DIVISION}
                items={divisions.map((division) => ({
                    id: division.div_nom_aff,
                    label: division.div_nom_aff,
                    data: division,
                    selected: selectedDivision?.div_nom_aff === division.div_nom_aff,
                }))}
                onToggle={() => {
                    if (expandedAccordions === AccordionType.DIVISION) {
                        setExpandedAccordions(false);
                    } else {
                        setExpandedAccordions(AccordionType.DIVISION);
                    }
                }}
                onItemClick={(item, items) => {
                    const division = items.find((i) => i.id === item);
                    if (division?.selected) {
                        setSelectedDivision(division?.data as Division);
                    } else {
                        setSelectedDivision(undefined);
                    }
                }}
                maxContentHeight={containerHeight ? containerHeight - 100 : 400}
                chipsProps={{
                    minWidthPerChip: "80px",
                    maxWidthPerChip: "150px",
                    widthPerChip: "fit-content",
                }}
            />
            <FilterAccordion
                title="Sections"
                expanded={expandedAccordions === AccordionType.SECTION}
                disabled={!selectedDivision}
                items={sections.map((section) => ({
                    id: section.plans_seq,
                    label: section.plans_nom,
                    data: section,
                    selected: selectedSection?.plans_seq === section.plans_seq,
                }))}
                onToggle={() => {
                    if (expandedAccordions === AccordionType.SECTION) {
                        setExpandedAccordions(false);
                    } else {
                        setExpandedAccordions(AccordionType.SECTION);
                    }
                }}
                onItemClick={(item, items) => {
                    const section = items.find((i) => i.id === item);
                    if (section?.selected) {
                        setSelectedSection(section?.data as Section);
                    } else {
                        setSelectedSection(undefined);
                    }
                }}
                maxContentHeight={containerHeight ? containerHeight - 100 : 400}
                chipsProps={{
                    minWidthPerChip: "80px",
                    maxWidthPerChip: "150px",
                    widthPerChip: "fit-content",
                }}
            />
        </Box>
    )
};

export const DashboardStoreAccordions: FunctionComponent = () => {
    const {
        expandedAccordions,
        magasins,
        divisions,
        selectedMagasin,
        selectedDivision,
        setSelectedMagasin,
        setSelectedDivision,
        setExpandedAccordions,
    } = useDashboardContext();

    const containerRef = useRef<HTMLDivElement>(null);
    const [containerHeight, setContainerHeight] = useState(0);

    useEffect(() => {
        const resizeObserverHandler = (entries: ResizeObserverEntry[]) => {
            for (let entry of entries) {
                const { height } = entry.contentRect;
                console.log("Container height:", height);
                setContainerHeight(height);
            }
        };

        const resizeObserver = new ResizeObserver(resizeObserverHandler);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }
        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <Box
            ref={containerRef}
            sx={{
                height: "100%",
            }}
        >
            <FilterAccordion
                title="Magasins"
                expanded={expandedAccordions === AccordionType.MAGASIN}
                items={magasins.map((magasin) => ({
                    id: magasin.mag_nom_aff,
                    label: magasin.mag_nom_aff,
                    data: magasin,
                    selected: selectedMagasin?.mag_nom_aff === magasin.mag_nom_aff,
                }))}
                onToggle={() => {
                    if (expandedAccordions === AccordionType.MAGASIN) {
                        setExpandedAccordions(false);
                    } else {
                        setExpandedAccordions(AccordionType.MAGASIN);
                    }
                }}
                onItemClick={(item, items) => {
                    const magasin = items.find((i) => i.id === item);
                    if (magasin?.selected) {
                        setSelectedMagasin(magasin?.data as Magasin);
                    } else {
                        setSelectedMagasin(undefined);
                    }
                }}
                maxContentHeight={containerHeight ? containerHeight - 100 : 400}
                chipsProps={{
                    minWidthPerChip: "80px",
                    maxWidthPerChip: "150px",
                    widthPerChip: "fit-content",
                }}
            />
            <FilterAccordion
                title="Divisions"
                expanded={expandedAccordions === AccordionType.DIVISION}
                disabled={!selectedMagasin}
                items={divisions.map((division) => ({
                    id: division.div_nom_aff,
                    label: division.div_nom_aff,
                    data: division,
                    selected: selectedDivision?.div_nom_aff === division.div_nom_aff,
                }))}
                onToggle={() => {
                    if (expandedAccordions === AccordionType.DIVISION) {
                        setExpandedAccordions(false);
                    } else {
                        setExpandedAccordions(AccordionType.DIVISION);
                    }
                }}
                onItemClick={(item, items) => {
                    const division = items.find((i) => i.id === item);
                    if (division?.selected) {
                        setSelectedDivision(division?.data as Division);
                    } else {
                        setSelectedDivision(undefined);
                    }
                }}
                maxContentHeight={containerHeight ? containerHeight - 100 : 400}
                chipsProps={{
                    minWidthPerChip: "80px",
                    maxWidthPerChip: "150px",
                    widthPerChip: "fit-content",
                }}
            />
        </Box>
    );
};
